import React, { FC, useEffect, useState, useRef, useMemo } from 'react'
import { InputGroup, Button, VStack, Spinner, Image } from '@chakra-ui/react'
import AutoComplete from 'react-google-autocomplete'
import { Icon } from '../icon'
import theme from '../../styles'
import { getCookie, setCookie } from '../../scenarios'
import { mapProvince, formatPostalCode, createProvinceObject, provinceObjectType } from '../../scenarios/utils'
import { useSelector } from 'react-redux'
import { selectAnswers, selectScenario, selectScenarioIsWaitingForAnswer } from '../../state'
import { useTranslation } from 'react-i18next'
import { getLocale } from '../../i18n/locale'
import { LoadingNewAnswer } from '../spinners/loading-new-answer'
import { cardImageMap } from '../../state/interfaces'

export interface SelectProps {
  onChange?: (values: string[]) => void
  placeholder?: string
  grandImage?: string
}

const addressComponentsForm: Record<string, string> = {
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'short_name',
  country: 'long_name',
  postal_code: 'short_name',
}

export const GoogleAutocomplete: FC<SelectProps> = ({ placeholder, onChange, grandImage }) => {
  const [rawAddress, setRawAddress] = useState('')
  const [placeId, setPlaceId] = useState('')
  const [itemsAlign, setItemsAlign] = useState('end')
  const [addressComponents, setAddressComponents] = useState<Record<string, string>>({})
  const [provinceObject, setProvinceObject] = useState<provinceObjectType | null>({
    name: {
      en: '',
      fr: '',
    },
    prepositions: {
      en: '',
      fr: '',
    },
  })

  const answers = useSelector(selectAnswers)
  const { t } = useTranslation()
  const locale = getLocale()
  const scenario = useSelector(selectScenario)
  const scenarioIsWaitingForAnswer = useSelector(selectScenarioIsWaitingForAnswer)
  const showCancelButton = scenario!.id === 'internet-f' || scenario!.id === 'internet-g'
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])

  useEffect(() => {
    if (getCookie('internetSearchPostalcode')) {
      answers &&
        answers['address-location'] &&
        answers['address-location'].includes('finishScenario') &&
        setItemsAlign('center')
    }
  }, [])

  useEffect(() => {
    if (answers['province'] && answers['province']![0]) {
      setProvinceObject(createProvinceObject(answers['province']![0]))
    }
  }, [answers['province']])

  const extractAddressComponents = (addressComponents: Record<string, string | Array<string>>[]) => {
    const address: Record<string, string> = {
      street_number: '',
      route: '',
      locality: '',
      administrative_area_level_1: '',
      country: '',
      postal_code: '',
    }

    for (let i = 0; i < addressComponents.length; i++) {
      const addressType = addressComponents[i].types[0]
      if (addressComponentsForm[addressType]) {
        address[addressType] = (addressComponents[i][addressComponentsForm[addressType]] as string).toLocaleLowerCase()
      }
    }
    setAddressComponents(address)
    saveAddressComponents(address)
  }

  const removeAccents = (str: string) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }

  const saveAddressComponents = ({
    street_number,
    route,
    locality,
    administrative_area_level_1,
    country,
    postal_code,
  }: Record<string, string>) => {
    setCookie('internetSearchCountry', removeAccents(country))
    setCookie('internetSearchLocality', removeAccents(locality))
    setCookie('internetSearchStreetName', removeAccents(route))
    setCookie('internetSearchStreetNumber', street_number)
    setCookie('internetSearchPostalcode', formatPostalCode(postal_code))
    setCookie('internetSearchProvince', mapProvince(administrative_area_level_1))
  }

  useEffect(() => {
    setCookie('placeId', placeId)
  }, [placeId])

  const [inputInUse, setInputInUse] = useState(false)
  const inputElement = useRef<HTMLInputElement>(null)

  return (
    <VStack spacing={0} alignItems="center" width={'full'} maxWidth={650}>
      <VStack spacing={6} marginY={8} width={'full'} maxWidth={650} alignItems={itemsAlign}>
        {answers && answers['address-location'] && answers['address-location'].includes('finishScenario') ? (
          <Spinner />
        ) : (
          <InputGroup
            display="grid"
            outline="none"
            borderRadius="md"
            variant="none"
            fontSize={{ base: 16, md: 22 }}
            paddingLeft={4}
            layerStyle={theme.LayerStyles.INPUT}
            borderColor={inputInUse ? 'green.200' : 'grey'}
            autoFocus
          >
            <AutoComplete
              ref={inputElement}
              apiKey={process.env.REACT_APP_GOOGLE_AUTOCOMPLETE_KEY}
              placeholder={placeholder}
              onPlaceSelected={({ address_components, place_id, formatted_address }) => {
                place_id && setPlaceId(place_id)
                address_components && extractAddressComponents(address_components)
                setRawAddress(formatted_address)
                setInputInUse(true)
              }}
              options={{
                types: ['geocode'],
                fields: ['address_components', 'formatted_address', 'place_id'],
                componentRestrictions: { country: 'ca' },
              }}
              // onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {setRawAddress(event.target.value), console.log(place)}}
              onClick={(e) => {
                if (!scenarioIsWaitingForAnswer) {
                  e.stopPropagation()
                  e.currentTarget.focus()
                  setInputInUse(true)
                }
              }}
              onBlur={() => setInputInUse(false)}
              onKeyPress={(ev) => {
                setInputInUse(true)
                if (!scenarioIsWaitingForAnswer) {
                  if (ev.key === 'Enter' && placeId) {
                    ev.preventDefault()
                    onChange && onChange([rawAddress, ...Object.values(addressComponents)])
                  }
                }
              }}
              onMouseEnter={() => {
                setInputInUse(true)
              }}
              onMouseLeave={() => {
                inputElement!.current!.value === '' ? setInputInUse(false) : setInputInUse(true)
              }}
            />
            {placeId && inputElement!.current!.value !== '' && (
              <Button
                role={'button'}
                position="absolute"
                borderRadius="md"
                layerStyle={theme.LayerStyles.BTN_INPUT_PILL}
                onClick={() => {
                  if (!scenarioIsWaitingForAnswer) {
                    onChange && onChange([rawAddress, ...Object.values(addressComponents)])
                  }
                }}
              >
                {scenarioIsWaitingForAnswer ? <LoadingNewAnswer /> : <Icon icon="arrow-right" color="white" />}
              </Button>
            )}
          </InputGroup>
        )}
        {showCancelButton && answers && !answers['address-location'] && (
          <Button
            role={'button'}
            borderRadius={'lg'}
            borderColor={'#888888'}
            onClick={() => {
              setItemsAlign('center')
              onChange && onChange(['No address'])
            }}
            fontSize={{ base: 14, md: 22 }}
            marginTop={10}
            textColor={'#888888'}
            tabIndex={0}
            _focus={{ borderColor: 'purple' }}
            _hover={{ borderColor: 'purple' }}
          >
            {t('Skip-and-search-for-all')} {provinceObject?.prepositions[locale]}
            {provinceObject?.name[locale]}
          </Button>
        )}
      </VStack>
      <Image src={image} alt="image" width={['60%', '45%']} />
    </VStack>
  )
}
